import { useEffect, useState } from "react";
import * as api from "./adminApi";
import { AdminShell, Btn, Card, Field, Row, Sel, Text, useAsync } from "./components";

const QUESTION_TYPES: Array<{ value: api.FeedbackQuestion["type"]; label: string }> = [
  { value: "RATING_5", label: "Rating (1–5)" },
  { value: "NPS_0_10", label: "Likelihood to recommend (0–10)" },
  { value: "TEXT", label: "Free text" },
  { value: "SINGLE_CHOICE", label: "Single choice" },
];

function emptyQuestion(): api.FeedbackQuestion {
  return { id: crypto.randomUUID(), text: "", type: "RATING_5" };
}

interface FormState {
  title: string;
  programmeId: string; // "" = global fallback (null)
  questions: api.FeedbackQuestion[];
  active: boolean;
}

function emptyForm(): FormState {
  return { title: "", programmeId: "", questions: [emptyQuestion()], active: true };
}

function formFromTemplate(t: api.FeedbackTemplateRow): FormState {
  return {
    title: t.title,
    programmeId: t.programmeId ?? "",
    questions: t.questions.length > 0 ? t.questions : [emptyQuestion()],
    active: t.active,
  };
}

function TemplateForm({
  programmes,
  editing,
  onSaved,
  onCancel,
}: {
  programmes: api.ProgrammeListRow[];
  editing: api.FeedbackTemplateRow | null;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<FormState>(editing ? formFromTemplate(editing) : emptyForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(editing ? formFromTemplate(editing) : emptyForm());
  }, [editing]);

  function setQuestion(i: number, patch: Partial<api.FeedbackQuestion>) {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)),
    }));
  }
  function addQuestion() {
    setForm((f) => ({ ...f, questions: [...f.questions, emptyQuestion()] }));
  }
  function removeQuestion(i: number) {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const questions = form.questions
        .filter((q) => q.text.trim())
        .map((q) =>
          q.type === "SINGLE_CHOICE"
            ? q
            : { id: q.id, text: q.text, type: q.type satisfies api.FeedbackQuestion["type"] },
        );
      if (editing) {
        await api.updateFeedbackTemplate(editing.id, { title: form.title, questions });
      } else {
        await api.createFeedbackTemplate({
          title: form.title,
          programmeId: form.programmeId || null,
          questions,
        });
      }
      onSaved();
      if (!editing) setForm(emptyForm());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const valid =
    form.title.trim().length > 0 && form.questions.some((q) => q.text.trim().length > 0);

  return (
    <div>
      <Field label="Template title">
        <Text
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
      </Field>
      <Field label="Programme (leave blank for a global fallback template)">
        <Sel
          value={form.programmeId}
          onChange={(e) => setForm((f) => ({ ...f, programmeId: String(e.target.value) }))}
          disabled={!!editing}
        >
          <option value="">All programmes (fallback)</option>
          {programmes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </Sel>
        {editing && (
          <p style={{ color: "#888", fontSize: "0.78rem", marginTop: "0.2rem" }}>
            Programme can only be set when a template is created.
          </p>
        )}
      </Field>
      <Field label="Questions">
        <div>
          {form.questions.map((q, i) => (
            <div
              key={q.id}
              style={{
                marginBottom: "0.6rem",
                padding: "0.6rem",
                border: "1px solid #eee",
                borderRadius: 8,
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <Text
                  value={q.text}
                  onChange={(e) => setQuestion(i, { text: e.target.value })}
                  placeholder="Question text"
                  style={{ flex: 1 }}
                />
                <Sel
                  value={q.type}
                  onChange={(e) =>
                    setQuestion(i, { type: e.target.value as api.FeedbackQuestion["type"] })
                  }
                  style={{ width: 220 }}
                >
                  {QUESTION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Sel>
                <Btn kind="danger" onClick={() => removeQuestion(i)}>
                  Remove
                </Btn>
              </div>
              {q.type === "SINGLE_CHOICE" && (
                <div style={{ marginTop: "0.4rem" }}>
                  <Text
                    value={(q.options ?? []).join(", ")}
                    onChange={(e) =>
                      setQuestion(i, {
                        options: e.target.value
                          .split(",")
                          .map((o) => o.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="Options, comma-separated"
                  />
                </div>
              )}
            </div>
          ))}
          <Btn kind="ghost" onClick={addQuestion}>
            Add question
          </Btn>
        </div>
      </Field>
      {error && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{error}</p>}
      <div style={{ display: "flex", gap: "0.6rem", marginTop: "0.6rem" }}>
        <Btn onClick={save} disabled={busy || !valid}>
          {busy ? "Saving…" : editing ? "Save template" : "Create template"}
        </Btn>
        {onCancel && (
          <Btn kind="ghost" onClick={onCancel}>
            Cancel
          </Btn>
        )}
      </div>
    </div>
  );
}

export function FeedbackTemplatesPage() {
  const { data: templates, error, reload } = useAsync(() => api.listFeedbackTemplates(), []);
  const { data: programmes } = useAsync(api.listProgrammes, []);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editing = (templates ?? []).find((t) => t.id === editingId) ?? null;

  return (
    <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Feedback templates">
      {error && <Card>{error}</Card>}

      <Card>
        <strong>Templates</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          Shown to the learner right after their certificate is generated — always skippable, never
          blocks the certificate.
        </p>
        {templates?.length === 0 && <p style={{ color: "#666" }}>No feedback templates yet.</p>}
        {templates?.map((t) => (
          <Row key={t.id}>
            <span
              onClick={() => setEditingId(t.id)}
              style={{ cursor: "pointer", color: "#1a1a2e" }}
              title="Click to edit"
            >
              {t.title}
              <span style={{ color: "#888" }}> · {t.programme?.title ?? "All programmes"}</span>
            </span>
            <Btn
              kind="ghost"
              onClick={async () => {
                await api.updateFeedbackTemplate(t.id, { active: !t.active });
                reload();
              }}
            >
              {t.active ? "Deactivate" : "Activate"}
            </Btn>
          </Row>
        ))}
      </Card>

      <Card>
        <strong>{editing ? "Edit template" : "New template"}</strong>
        <div style={{ marginTop: "0.75rem" }}>
          <TemplateForm
            programmes={programmes ?? []}
            editing={editing}
            onSaved={() => {
              setEditingId(null);
              reload();
            }}
            onCancel={editing ? () => setEditingId(null) : undefined}
          />
        </div>
      </Card>
    </AdminShell>
  );
}
